import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { fromNetwork } from '@adornis/baseql/symbols.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Dependencies } from '@adornis/materialized-views/decorators/dependency.js';
import { Aufenthalt, Weekday } from '@chancenwerk/db/enums/enums.js';
import { DateTime } from 'luxon';
import type { ChancenDynamicClassProduct } from './ChancenDynamicClassProduct.js';

export const PRODUCT_BLACKLIST = {
  _id: 1,
  ragYearInformation: 1,
  durchfuehrungsTage: {
    _class: 1,
    cid: 1, // Missing translations, so we just hide this field
  },
};

/**
 * @entity DurchfuehrungsTag
 */
@Entity()
export class DurchfuehrungsTag extends AdornisEntity {
  public static override _class = 'DurchfuehrungsTag';

  @Field(type => String)
  public cid: Maybe<String>;

  @Field(type => String)
  public info: Maybe<String>;

  @Field(type => String)
  public wochentag: Maybe<Weekday>;

  @Field(type => DateTime)
  public startZeit: Maybe<DateTime>;

  @Field(type => DateTime)
  public endZeit: Maybe<DateTime>;

  @Field(type => [String], { default: v => v ?? [] })
  public locations!: string[];

  public override toString(): string {
    return this.wochentag ?? '';
  }

  public static override resolveID(entity: DurchfuehrungsTag): string {
    // ! We use DurchfuehrungsTag also on the student for the choosen intensive courses, which is used
    // ! in a print component. In this case we can't build a unique ID based on the fields of the entity.
    // ! This is a dirty hack, but it works for now.
    try {
      entity.locations;
    } catch (e) {
      return A.getGloballyUniqueID();
    }

    const id = (entity.wochentag ?? A.getGloballyUniqueID()) + '_' + (entity.locations ?? []).join('_');

    try {
      entity.cid;
    } catch (e) {
      return id;
    }

    return id + '_' + entity.cid;
  }

  public sameTime(starZeit: Maybe<DateTime>, endZeit: Maybe<DateTime>) {
    return (
      starZeit &&
      this.startZeit?.hasSame(starZeit, 'hour') &&
      this.startZeit.hasSame(starZeit, 'minute') &&
      endZeit &&
      this.endZeit?.hasSame(endZeit, 'hour') &&
      this.endZeit.hasSame(endZeit, 'minute')
    );
  }
}

export enum RAGGraduationTypes {
  HAUPTSCHULE = 'Hauptschulabschluss',
  MITTLERER_ABSCHLUSS = 'Mittlerer Schulabschluss',
  ABITUR = 'Abitur',
}

export enum RAGChancenAbschlussTypes {
  CRASHCOURSE = 'CHANCENabschluss-Crashkurs',
  INDIVIDUELL = 'individueller CHANCENabschluss-Kurs in der Schule',
  WOECHENTLICH = 'wöchentlicher CHANCENabschluss-Kurs',
}

/**
 * @entity RAGYearInformation
 */
@Entity()
export class RAGYearInformation extends AdornisEntity {
  public static override _class = 'RAGYearInformation';

  @Field(type => ID, { default: v => v ?? A.getGloballyUniqueID() })
  public _id: Maybe<string>;

  @Field(type => String)
  public numberOfCourses: Maybe<string>;

  @Field(type => String)
  public numberOfStudents: Maybe<string>;

  @Field(type => String)
  public numberOfBooklets: Maybe<string>;

  @Field(type => String)
  public numberOfTeacherAccounts: Maybe<string>;

  @Field(type => String)
  public year: Maybe<string>;

  @Field(type => String)
  public graduationType: Maybe<keyof RAGGraduationTypes>;

  @Field(type => String)
  public chancenAbschlussType: Maybe<keyof RAGChancenAbschlussTypes>;
}

/**
 * @entity Product
 */
@Entity()
export class Product extends MongoEntity {
  public static override _class = 'Product';
  public static override _collectionName = 'products';

  constructor(doc: Partial<EntityData<Product>>) {
    super(doc);

    // TODO better to override resolveID instead, but I cannot do a full testrun right now
    this._id ??= A.getGloballyUniqueID();
  }

  // static override resolveID(entity: Product) {
  //   return entity._id ?? (entity._id = A.getGloballyUniqueID());
  // }

  @Field(type => String, { default: v => v ?? A.getGloballyUniqueID() })
  public override _id!: string;

  @Dependencies<typeof Product>({
    classes: async () => [(await import('./ChancenDynamicClassProduct')).ChancenDynamicClassProduct],
    ids: async function (this: PartialEntityData<Product>) {
      const dynamicEntity = await import('./ChancenDynamicClassProduct');
      const col = await getRawCollection<ChancenDynamicClassProduct>(
        dynamicEntity.ChancenDynamicClassProduct._collectionName,
      );
      const dynamicClass = await col.findOne({ class: this._class });
      return [dynamicClass?._id];
    },
  })
  @Field(type => String, {
    resolve: function (this: Product) {
      return async () => {
        const dynamicEntity = await import('./ChancenDynamicClassProduct');
        const col = await getRawCollection<ChancenDynamicClassProduct>(
          dynamicEntity.ChancenDynamicClassProduct._collectionName,
        );
        const dynamicClass = await col.findOne({ class: this._class });
        return dynamicClass?.name;
      };
    },
  })
  public name: Maybe<string>;

  @Dependencies<typeof Product>({
    classes: async () => [(await import('./ChancenDynamicClassProduct')).ChancenDynamicClassProduct],
    ids: async function (this: PartialEntityData<Product>) {
      const dynamicEntity = await import('./ChancenDynamicClassProduct');
      const col = await getRawCollection<ChancenDynamicClassProduct>(
        dynamicEntity.ChancenDynamicClassProduct._collectionName,
      );
      const dynamicClass = await col.findOne({ class: this._class });
      return [dynamicClass?._id];
    },
  })
  @Field(type => String, {
    resolve: function (this: Product) {
      return async () => {
        const dynamicEntity = await import('./ChancenDynamicClassProduct');
        const col = await getRawCollection<ChancenDynamicClassProduct>(
          dynamicEntity.ChancenDynamicClassProduct._collectionName,
        );
        const dynamicClass = await col.findOne({ class: this._class });
        return dynamicClass?.kuerzel;
      };
    },
  })
  public kuerzel: Maybe<string>;

  /** @deprecated */
  public static capacitySelctionSet(): BaseQLSelectionSet<Product> {
    return {};
  }

  @Field(type => String)
  public betreuungsverhaeltnis: Maybe<string>;

  @Field(type => String)
  public aufenthalt: Maybe<Aufenthalt>;

  @Field(type => String)
  public betreuungsintensitaet: Maybe<string>;

  @Field(type => [RAGYearInformation], { default: v => v ?? [] })
  public ragYearInformation: Maybe<RAGYearInformation[]>;

  override toObject(
    filterResolved?: boolean,
    visited?: Set<AdornisEntity>,
  ): EntityData<this> & { _id: string; [fromNetwork]?: boolean | undefined } {
    const result = super.toObject(filterResolved, visited);
    if (filterResolved) {
      delete result.name;
      delete result.kuerzel;
    }
    return result;
  }

  override toString(): string {
    return this.kuerzel ?? '';
  }
}
